import React, { useState } from 'react';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, TextField, makeStyles } from '@material-ui/core';
import type { FormField } from '../interfaces';
import { getLocalizedLabel } from '../utils';
import { ActionButton, Tooltip } from '@klp/design';
import { string } from 'yup';
import { IconName, Illustration } from '@klp/design/Illustrations';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

const useStyles = makeStyles((theme) => ({
    buttonsContainer: {
        display: 'flex',
    },
    editIcon: {
        marginLeft: theme.spacing(4),
    },
}));

export const LinkSelectorEditable = ({
    field,
    isRequired,
    isReadOnly,
}: {
    field: FormField;
    isRequired: boolean;
    isReadOnly: boolean;
}) => {
    const { t } = useTranslation();
    const localClasses = useStyles();
    const formFieldClasses = getFormFieldStyles();
    const yupUrlValidator = string().url();
    const checkValidLink = (value: string | undefined) => {
        if (!value || value === '') return;

        if (!yupUrlValidator.isValidSync(value)) {
            return t('project.errors.invalidLink');
        }

        return;
    };
    const validLink = field.value && yupUrlValidator.isValidSync(field.value);
    const [isEditMode, setIsEditMode] = useState(!validLink);

    const [formikField, meta] = useField<string | undefined>({
        name: field.id as string,
        validate: checkValidLink,
    });

    const handleEditReset = () => {
        if (yupUrlValidator.isValidSync(formikField.value)) {
            setIsEditMode(false);
        }
    };

    const linkButton = (
        <ActionButton
            actionType="external"
            title={formikField.value}
            translation={getLocalizedLabel(
                `project.formFields.${field.id}`,
                field.label,
                field.id,
            )}
            onClick={() => window.open(formikField.value, '_blank')}
        />
    );

    if (isReadOnly) return field.value ? linkButton : null;

    return (
        <>
            {isEditMode ? (
                <TextField
                    variant="outlined"
                    fullWidth={true}
                    className={classNames({
                        [formFieldClasses.editableField]: !isReadOnly,
                        [formFieldClasses.readOnlyField]: isReadOnly,
                    })}
                    {...formikField}
                    required={isRequired}
                    disabled={isReadOnly}
                    label={getLocalizedLabel(
                        `project.formFields.${field?.id}`,
                        field?.label,
                        field?.id,
                    )}
                    InputProps={{
                        endAdornment: formikField.value && (
                            <Tooltip translation="general.save">
                                <IconButton
                                    aria-label="save"
                                    onClick={handleEditReset}
                                    color="primary"
                                >
                                    <Illustration icon={IconName.CHECK} />
                                </IconButton>
                            </Tooltip>
                        ),
                    }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    helperText={meta.touched ? meta.error : ''}
                    error={meta.touched && Boolean(meta.error)}
                />
            ) : (
                <Box className={localClasses.buttonsContainer}>
                    {linkButton}
                    <Tooltip translation="general.edit">
                        <IconButton
                            aria-label="edit"
                            onClick={() => setIsEditMode(true)}
                            color="primary"
                            className={localClasses.editIcon}
                        >
                            <Illustration icon={IconName.EDIT} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </>
    );
};
